import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, BadgeCheck, Gift, Headphones, Mail, Facebook, Instagram, Youtube, Linkedin, ArrowUpRight, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { newsletterPerks, footerLinks, LOGO_URL, EBG_LOGO_URL } from "../mock";
import { submitEnquiry } from "../api";

const NAVY = "#1B1F5C";
const perkIcons = { Truck: ShieldCheck, Timer: BadgeCheck, PackageOpen: Gift, Headphones };

const XIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
);

const linkPaths = {
  "Shop by Age": "/shop-by-age",
  "Toys & Games": "/toys-games",
  "Learning & Education": "/learning-education",
  "Gifts": "/gifts",
  "All Collections": "/categories",
  "Store Locator": "/store-locator",
  "FAQs": "/faqs",
  "Contact Us": "/contact-us",
  "Help Center": "/help-center",
  "Our Story": "/our-story",
  "Our Franchise": "/franchise",
  "Blog": "/blog",
};

const FooterLink = ({ label }) => (
  <Link to={linkPaths[label] || "/"} className="text-[12.5px] font-semibold text-gray-500 hover:text-[#35C3C1] transition-colors" data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    {label}
  </Link>
);

const Footer = ({ hideNewsletter = false }) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError("Please enter your email address");
      return;
    }
    if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(trimmed)) {
      setEmailError("Please enter a valid email address (e.g. name@example.com)");
      return;
    }
    setEmailError("");
    try {
      await submitEnquiry({ name: "Newsletter Subscriber", phone: "-", email: trimmed, message: "Newsletter subscription", type: "newsletter" });
      toast.success("Welcome to the Adhira & Appa Toys family!");
      setEmail("");
    } catch {
      toast.error("Could not subscribe right now. Please try again.");
    }
  };

  return (
    <>
      {/* Newsletter banner */}
      {!hideNewsletter && (
      <section className="py-8 md:py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="rounded-3xl px-6 md:px-10 py-8 md:py-10 relative overflow-hidden" style={{ backgroundColor: NAVY }}>
            {/* doodle stars */}
            <svg className="absolute top-6 left-[38%] w-6 h-6 text-[#35C3C1] hidden md:block" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>
            <svg className="absolute bottom-6 right-8 w-5 h-5 text-[#FCD44E]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>

            <div className="grid lg:grid-cols-[1.1fr_1.6fr] gap-8 items-center">
              <div className="text-left relative z-10">
                <h3 className="font-display font-extrabold text-xl md:text-2xl text-white">Join Adhira & Appa Toys Family!</h3>
                <p className="mt-1.5 text-[12.5px] font-medium text-white/70 max-w-xs leading-relaxed">
                  Get exclusive offers, new arrivals & play ideas straight to your inbox.
                </p>
                <form onSubmit={handleSubscribe} noValidate className="mt-4 flex max-w-sm">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(""); }}
                    placeholder="Enter your email"
                    data-testid="newsletter-email-input"
                    className="flex-1 h-11 px-4 rounded-l-full bg-white text-sm outline-none text-gray-700 min-w-0"
                  />
                  <button type="submit" data-testid="newsletter-submit-btn" className="h-11 px-5 rounded-r-full bg-[#FCD44E] text-[12px] font-extrabold tracking-wide hover:brightness-105 transition-colors" style={{ color: NAVY }}>
                    SUBSCRIBE
                  </button>
                </form>
                {emailError && <p className="mt-2 text-[11.5px] font-bold text-[#FCD44E]" data-testid="newsletter-email-error">{emailError}</p>}
                <Mail className="absolute -bottom-2 right-2 text-white/20 hidden md:block" size={44} strokeWidth={1.2} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {newsletterPerks.map((p) => {
                  const Icon = perkIcons[p.icon];
                  return (
                    <div key={p.title} className="flex flex-col items-center text-center">
                      <span className="h-14 w-14 rounded-full border-2 border-white/25 flex items-center justify-center mb-3">
                        <Icon size={24} className="text-white" strokeWidth={1.6} />
                      </span>
                      <p className="text-[13px] font-extrabold text-white">{p.title}</p>
                      <p className="text-[11.5px] font-medium text-white/60 mt-0.5">{p.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-10 md:py-14">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 text-left">
            {/* Brand col */}
            <div className="col-span-2 md:col-span-3 lg:col-span-1">
              <img src={LOGO_URL} alt="Adhira & Appa Toys" className="h-28 w-auto" />
              <p className="mt-4 text-[12.5px] font-medium text-gray-500 leading-relaxed max-w-[230px]">
                Adhira & Appa Toys is your trusted destination for premium quality toys that inspire play, learning and happiness.
              </p>
              {/* EBG Group card */}
              <a
                href="https://theebg.com"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 flex items-center gap-4 rounded-2xl px-5 py-4 w-fit max-w-[260px] hover:shadow-lg hover:-translate-y-0.5 transition-transform duration-200 group"
                style={{ backgroundColor: NAVY }}
              >
                <img src={EBG_LOGO_URL} alt="EBG Group" className="h-10 w-auto object-contain shrink-0" />
                <div className="text-left leading-snug">
                  <p className="text-[13px] font-bold text-white">
                    An <span className="text-[#D4AF6A]">EBG</span> Group
                  </p>
                  <p className="text-[13px] font-bold text-white">Company</p>
                  <p className="mt-1 text-[11px] font-semibold text-white/60 inline-flex items-center gap-0.5 group-hover:text-[#D4AF6A] transition-colors">
                    theebg.com <ArrowUpRight size={11} />
                  </p>
                </div>
              </a>
            </div>
            {/* Shop */}
            <div>
              <p className="text-[13.5px] font-extrabold mb-4" style={{ color: NAVY }}>Shop</p>
              <ul className="space-y-2.5">
                {footerLinks.shop.map((l) => (
                  <li key={l}><FooterLink label={l} /></li>
                ))}
              </ul>
            </div>
            {/* Help */}
            <div>
              <p className="text-[13.5px] font-extrabold mb-4" style={{ color: NAVY }}>Help</p>
              <ul className="space-y-2.5">
                {footerLinks.help.map((l) => (
                  <li key={l}><FooterLink label={l} /></li>
                ))}
              </ul>
            </div>
            {/* About */}
            <div>
              <p className="text-[13.5px] font-extrabold mb-4" style={{ color: NAVY }}>About Us</p>
              <ul className="space-y-2.5">
                {footerLinks.about.map((l) => (
                  <li key={l}><FooterLink label={l} /></li>
                ))}
              </ul>
            </div>
            {/* Connect */}
            <div>
              <p className="text-[13.5px] font-extrabold mb-4" style={{ color: NAVY }}>Connect With Us</p>
              <div className="flex items-center gap-2.5 flex-wrap">
                <a href="https://www.facebook.com/profile.php?id=61593186835570" target="_blank" rel="noopener noreferrer" data-testid="footer-facebook-link" className="h-9 w-9 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:scale-110 transition-transform" aria-label="Facebook"><Facebook size={16} /></a>
                <a href="https://www.instagram.com/adhiraandappa__toys/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="h-9 w-9 rounded-full text-white flex items-center justify-center hover:scale-110 transition-transform" style={{ background: "radial-gradient(circle at 30% 110%, #FDB35D 0%, #E1306C 55%, #C13584 100%)" }} aria-label="Instagram"><Instagram size={16} /></a>
                <a href="https://www.youtube.com/@AdhiraAppa_Toys" target="_blank" rel="noopener noreferrer" data-testid="footer-youtube-link" className="h-9 w-9 rounded-full bg-[#FF0000] text-white flex items-center justify-center hover:scale-110 transition-transform" aria-label="YouTube"><Youtube size={16} /></a>
                <a href="https://www.linkedin.com/company/135907758/" target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin-link" className="h-9 w-9 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:scale-110 transition-transform" aria-label="LinkedIn"><Linkedin size={16} /></a>
                <a href="https://x.com/AdhiraAppatoys1" target="_blank" rel="noopener noreferrer" data-testid="footer-x-link" className="h-9 w-9 rounded-full bg-black text-white flex items-center justify-center hover:scale-110 transition-transform" aria-label="X (Twitter)"><XIcon size={14} /></a>
              </div>
              <div className="mt-5 space-y-2.5">
                <p className="flex items-start gap-2 text-[12px] font-semibold text-gray-500 leading-relaxed" data-testid="footer-address">
                  <MapPin size={14} className="text-[#35C3C1] shrink-0 mt-0.5" />
                  <span>Knowledge City, 12th Floor, Dallas Center, Hitech City Main Rd, Silpa Gram Craft Village, Madhapur, Rai Durg, Hyderabad, Telangana 500032</span>
                </p>
                <a href="tel:+918828131962" data-testid="footer-phone-link" className="flex items-center gap-2 text-[12.5px] font-extrabold hover:text-[#35C3C1] transition-colors" style={{ color: NAVY }}>
                  <Phone size={14} className="text-[#35C3C1]" /> +91 88281 31962
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col md:flex-row items-center justify-between gap-2">
            <p className="text-[11.5px] font-semibold text-gray-400">© 2026 Adhira & Appa Toys. All Rights Reserved.</p>
            <p className="text-[11.5px] font-semibold text-gray-400">
              Design & Development by{" "}
              <a href="https://www.reachvel.com" target="_blank" rel="noopener noreferrer" className="font-extrabold text-[#35C3C1] hover:underline">
                Reachvel
              </a>
            </p>
            <div className="flex items-center gap-2 text-[11.5px] font-semibold text-gray-400">
              <Link to="/privacy-policy" className="hover:text-[#35C3C1] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <span>|</span>
              <Link to="/terms" className="hover:text-[#35C3C1] transition-colors" data-testid="footer-terms-link">Terms & Conditions</Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
